import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AccountIconName, InlineEditSaveFn } from 'src/app/models/accountSettings.model';

@Component({
    standalone: false,
    selector: 'app-account-inline-edit-chip',
    templateUrl: './account-inline-edit-chip.component.html',
    styleUrls: ['./account-inline-edit-chip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountInlineEditChipComponent implements OnDestroy {
    @Input() label = '';
    @Input() value: string | null = null;
    @Input() icon: AccountIconName = 'pen';
    @Input() placeholder = '';
    @Input() inputType: 'text' | 'tel' = 'text';
    @Input() maxLength: number | null = null;
    @Input() editLabel = '';
    @Input() saveLabel = '';
    @Input({ required: true }) saveFn!: InlineEditSaveFn;

    @ViewChild('editInput')
    private set editInput(input: ElementRef<HTMLInputElement> | undefined) {
        input?.nativeElement.focus();
    }

    editing = false;
    saving = false;
    draft = '';

    private readonly destroy$ = new Subject<void>();

    constructor(private cdr: ChangeDetectorRef) {}

    startEdit(): void {
        this.draft = this.value ?? '';
        this.editing = true;
    }

    cancel(): void {
        if (this.saving) return;
        this.editing = false;
    }

    onDraftInput(event: Event): void {
        this.draft = (event.target as HTMLInputElement).value;
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.cancel();
        }
    }

    save(): void {
        if (this.saving) return;
        const value = this.draft.trim();
        if (value === (this.value ?? '')) {
            this.editing = false;
            return;
        }

        this.saving = true;
        this.saveFn(value)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.saving = false;
                    this.editing = false;
                    this.cdr.markForCheck();
                },
                error: () => {
                    this.saving = false;
                    this.cdr.markForCheck();
                },
                complete: () => {
                    this.saving = false;
                    this.cdr.markForCheck();
                },
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
